import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, CreditCard, RefreshCcw, ShieldCheck, Wallet } from "lucide-react";

import {
  createWalletCashInSession,
  fetchMyWallet,
  fetchWalletCashIn,
} from "../../services/api";
import { ListSkeleton, SkeletonBlock } from "../../components/skeletons/ShopperSkeletons";

const PENDING_WALLET_CASH_IN_KEY = "pending_wallet_cash_in";
const MAX_WALLET_CASH_IN_AMOUNT = 42949672.95;

function createClientRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `wallet_cashin_${crypto.randomUUID()}`;
  }

  return `wallet_cashin_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `₱ ${amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function normalizeAmountInput(value) {
  const sanitized = String(value || "").replace(/[^\d.]/g, "");
  const [wholePart, ...decimalParts] = sanitized.split(".");
  const whole = wholePart.slice(0, 8);

  if (decimalParts.length === 0) {
    return whole;
  }

  const decimal = decimalParts.join("").slice(0, 2);
  return `${whole}.${decimal}`;
}

function parseAmountInput(value) {
  const amount = Number.parseFloat(String(value || "").trim());
  if (!Number.isFinite(amount)) return Number.NaN;
  return Number(amount.toFixed(2));
}

function getPaymentMethodLabel(method) {
  const normalized = String(method || "").trim().toUpperCase();

  if (normalized === "GCASH") return "GCash";
  if (normalized === "PAYMAYA") return "Maya";
  if (normalized === "CARD") return "Credit / Debit Card";
  if (normalized === "GRAB_PAY") return "GrabPay";
  if (normalized === "BILLEASE") return "BillEase";

  return normalized || null;
}

function toCashInErrorMessage(err) {
  const rawMessage = String(err?.message || "").trim();

  if (rawMessage.includes("CHECKOUT_REQUEST_LOCK_TIMEOUT")) {
    return "Your cash-in session is still being prepared. Please wait a moment, then try again.";
  }

  if (
    rawMessage.includes("PAYMENT_PROVIDER_NOT_CONFIGURED")
    || rawMessage.includes("PAYMONGO_CHECKOUT_URLS_NOT_CONFIGURED")
  ) {
    return "Wallet cash-in is temporarily unavailable right now. Please try again later.";
  }

  if (rawMessage.includes("Cash-in amount must be greater than zero")) {
    return "Enter an amount greater than zero to continue.";
  }

  if (rawMessage.includes("Cash-in amount exceeds the maximum supported value")) {
    return `The maximum wallet cash-in amount is ${formatCurrency(MAX_WALLET_CASH_IN_AMOUNT)}.`;
  }

  if (rawMessage.includes("Invalid cash-in request id")) {
    return "Your cash-in request expired before it could start. Please try again.";
  }

  return rawMessage || "Unable to start wallet cash-in right now.";
}

function readPendingCashIn() {
  try {
    const raw = sessionStorage.getItem(PENDING_WALLET_CASH_IN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const cashInId = Number(parsed?.cashInId);
    if (!Number.isInteger(cashInId) || cashInId <= 0) return null;

    if (parsed?.createdAt) {
      const ageMs = Date.now() - new Date(parsed.createdAt).getTime();
      if (!Number.isFinite(ageMs) || ageMs > 2 * 60 * 60 * 1000) {
        sessionStorage.removeItem(PENDING_WALLET_CASH_IN_KEY);
        return null;
      }
    }

    return {
      cashInId,
      clientRequestId: parsed?.clientRequestId || null,
      createdAt: parsed?.createdAt || null,
    };
  } catch {
    return null;
  }
}

function writePendingCashIn(payload) {
  try {
    sessionStorage.setItem(PENDING_WALLET_CASH_IN_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

function clearPendingCashIn() {
  try {
    sessionStorage.removeItem(PENDING_WALLET_CASH_IN_KEY);
  } catch {
    // ignore storage failures
  }
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return `${date.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })} • ${date.toLocaleTimeString("en-PH", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function getTransactionTitle(entry) {
  if (entry?.transactionType === "CASH_IN") {
    return "Wallet Cash-In";
  }

  if (entry?.transactionType === "REFUND_CREDIT") {
    return "Return / Refund Wallet Credit";
  }

  if (entry?.transactionType === "CHECKOUT_PAYMENT") {
    return "Wallet Payment for Checkout";
  }

  return "Wallet Transaction";
}

function getTransactionSubtitle(entry) {
  const meta = entry?.meta || entry?.meta_json || null;
  const orderReference = meta?.sellerOrderReference || meta?.orderReference || null;
  const isCheckoutDebit = entry?.transactionType === "CHECKOUT_PAYMENT";

  if (entry?.transactionType === "CASH_IN") {
    const paymentMethodLabel = meta?.paymentMethodLabel || getPaymentMethodLabel(meta?.paymentMethod);
    if (paymentMethodLabel) {
      return `Funds added via ${paymentMethodLabel}.`;
    }

    return "Funds added to your shopper credit after PayMongo confirmation.";
  }

  if (orderReference) {
    return isCheckoutDebit ? `Paid using shopper credit for ${orderReference}` : `Seller order #${orderReference}`;
  }

  if (entry?.referenceOrderId) {
    return isCheckoutDebit ? `Shopper credit payment for order #${entry.referenceOrderId}` : `Order #${entry.referenceOrderId}`;
  }

  if (isCheckoutDebit) {
    return "Paid successfully with shopper credit during checkout.";
  }

  return "Approved refund credited to your shopper wallet.";
}

function getCashInBanner(cashIn) {
  if (!cashIn) return null;

  const normalizedStatus = String(cashIn.status || "").toUpperCase();
  const amountLabel = formatCurrency(cashIn.amount || 0);
  const paymentMethodLabel = getPaymentMethodLabel(cashIn.paymentMethod);

  if (normalizedStatus === "PAID") {
    return {
      tone: "success",
      title: "Cash-in confirmed",
      message: paymentMethodLabel
        ? `${amountLabel} was added to your shopper credit via ${paymentMethodLabel}.`
        : `${amountLabel} was added to your shopper credit after PayMongo confirmation.`,
    };
  }

  if (normalizedStatus === "PENDING") {
    return {
      tone: "pending",
      title: "Waiting for payment confirmation",
      message: "Your cash-in is still syncing with PayMongo. Your shopper credit only updates after a confirmed successful payment.",
    };
  }

  if (normalizedStatus === "CANCELLED") {
    return {
      tone: "neutral",
      title: "Cash-in was cancelled",
      message: "No funds were added to your shopper credit.",
    };
  }

  if (normalizedStatus === "EXPIRED") {
    return {
      tone: "neutral",
      title: "Cash-in session expired",
      message: "The PayMongo checkout session expired before payment completed. No funds were added.",
    };
  }

  return {
    tone: "error",
    title: "Cash-in failed",
    message: "PayMongo did not confirm this payment. No funds were added to your shopper credit.",
  };
}

export default function WalletPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wallet, setWallet] = useState(null);
  const [cashInAmount, setCashInAmount] = useState("");
  const [cashInSubmitting, setCashInSubmitting] = useState(false);
  const [cashInFormError, setCashInFormError] = useState("");
  const [cashInStatusError, setCashInStatusError] = useState("");
  const [activeCashIn, setActiveCashIn] = useState(null);
  const [trackedCashInId, setTrackedCashInId] = useState(null);
  const [cashInSyncing, setCashInSyncing] = useState(false);
  const activeClientRequestIdRef = useRef(null);

  const transactions = useMemo(
    () => (Array.isArray(wallet?.transactions) ? wallet.transactions : []),
    [wallet]
  );

  const cashInBanner = useMemo(() => getCashInBanner(activeCashIn), [activeCashIn]);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await fetchMyWallet({ limit: 30 });
      setWallet(data || null);
    } catch (err) {
      setError(err?.message || "Unable to load your shopper wallet right now.");
      setWallet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const syncCashIn = useCallback(async (cashInId, { refreshWalletOnPaid = true } = {}) => {
    const normalizedCashInId = Number(cashInId);
    if (!Number.isInteger(normalizedCashInId) || normalizedCashInId <= 0) return null;

    setCashInSyncing(true);
    setCashInStatusError("");

    try {
      const data = await fetchWalletCashIn(normalizedCashInId);
      setActiveCashIn(data || null);
      setTrackedCashInId(normalizedCashInId);

      const normalizedStatus = String(data?.status || "").toUpperCase();
      if (normalizedStatus && normalizedStatus !== "PENDING") {
        clearPendingCashIn();
        activeClientRequestIdRef.current = null;
      }

      if (normalizedStatus === "PAID") {
        setCashInAmount("");
      }

      if (refreshWalletOnPaid && normalizedStatus === "PAID") {
        await loadWallet();
      }

      return data;
    } catch (err) {
      setCashInStatusError(err?.message || "Unable to verify your cash-in status right now.");
      return null;
    } finally {
      setCashInSyncing(false);
    }
  }, [loadWallet]);

  const handleRefresh = useCallback(async () => {
    await loadWallet();

    if (trackedCashInId) {
      await syncCashIn(trackedCashInId, { refreshWalletOnPaid: false });
    }
  }, [loadWallet, syncCashIn, trackedCashInId]);

  const handleCashInSubmit = async (event) => {
    event.preventDefault();
    if (cashInSubmitting) return;

    const amount = parseAmountInput(cashInAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setCashInFormError("Enter the exact amount you want to add to your shopper credit.");
      return;
    }

    if (amount > MAX_WALLET_CASH_IN_AMOUNT) {
      setCashInFormError(`The maximum wallet cash-in amount is ${formatCurrency(MAX_WALLET_CASH_IN_AMOUNT)}.`);
      return;
    }

    if (!activeClientRequestIdRef.current) {
      activeClientRequestIdRef.current = createClientRequestId();
    }

    setCashInSubmitting(true);
    setCashInFormError("");
    setCashInStatusError("");

    try {
      const session = await createWalletCashInSession({
        amount,
        clientRequestId: activeClientRequestIdRef.current,
      });

      if (!session?.cashInId) {
        throw new Error("Unable to create a wallet cash-in session.");
      }

      setTrackedCashInId(session.cashInId);
      setActiveCashIn(session);

      writePendingCashIn({
        cashInId: session.cashInId,
        clientRequestId: activeClientRequestIdRef.current,
        createdAt: new Date().toISOString(),
      });

      if (String(session?.status || "").toUpperCase() === "PAID") {
        clearPendingCashIn();
        activeClientRequestIdRef.current = null;
        setCashInAmount("");
        await loadWallet();
        return;
      }

      if (!session?.checkoutUrl) {
        throw new Error("Unable to start PayMongo checkout right now.");
      }

      window.location.assign(session.checkoutUrl);
    } catch (err) {
      activeClientRequestIdRef.current = null;
      setCashInFormError(toCashInErrorMessage(err));
    } finally {
      setCashInSubmitting(false);
    }
  };

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    const queryCashInId = Number(searchParams.get("cashInId"));
    if (Number.isInteger(queryCashInId) && queryCashInId > 0) {
      setTrackedCashInId(queryCashInId);
      return;
    }

    const pendingCashIn = readPendingCashIn();
    if (pendingCashIn?.cashInId) {
      setTrackedCashInId(pendingCashIn.cashInId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!trackedCashInId) return undefined;

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;

    const pollCashIn = async () => {
      const result = await syncCashIn(trackedCashInId);
      if (cancelled || !result) return;

      const normalizedStatus = String(result.status || "").toUpperCase();
      if (normalizedStatus === "PENDING" && attempts < 9) {
        attempts += 1;
        timeoutId = window.setTimeout(pollCashIn, 3000);
      }
    };

    pollCashIn();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [trackedCashInId, syncCashIn]);

  const cashInBannerTone = cashInBanner?.tone === "success"
    ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200"
    : cashInBanner?.tone === "error"
      ? "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200"
      : cashInBanner?.tone === "pending"
        ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
        : "border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-200";
  const initialWalletLoading = loading && wallet == null && !error;

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-[1.75rem] border border-neutral-200 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 px-4 py-5 text-white shadow-sm dark:border-neutral-800 sm:px-5 sm:py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">
              <Wallet size={12} />
              Shopper Credit
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-[2rem]">
              {initialWalletLoading ? <SkeletonBlock className="h-9 w-44 rounded-lg bg-white/20 dark:bg-white/15" /> : formatCurrency(wallet?.availableBalance || 0)}
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/75">
              Track wallet cash-ins, approved refund credits, and shopper-credit payments in one real balance and history view.
            </p>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading || cashInSyncing}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw size={14} className={loading || cashInSyncing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </section>

      <section className="rounded-[1.65rem] border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="p-4 sm:p-5">
          {cashInBanner ? (
            <div className={`mb-4 rounded-2xl border px-4 py-3 ${cashInBannerTone}`}>
              <div className="text-sm font-semibold">{cashInBanner.title}</div>
              <p className="mt-1 text-sm leading-relaxed">{cashInBanner.message}</p>
            </div>
          ) : null}

          {cashInStatusError ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              {cashInStatusError}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <form
              onSubmit={handleCashInSubmit}
              className="rounded-[1.5rem] border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-800/40 sm:p-5"
            >
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Cash In Shopper Credit
              </div>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
                Enter the exact amount you want credited, then continue to a secure PayMongo checkout for GCash, Maya, or credit and debit cards.
              </p>

              <label className="mt-5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
                Cash-In Amount
              </label>

              <div className="mt-2 flex items-center rounded-2xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900">
                <span className="pl-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">₱</span>
                <input
                  value={cashInAmount}
                  onChange={(event) => {
                    setCashInAmount(normalizeAmountInput(event.target.value));
                    if (cashInFormError) setCashInFormError("");
                  }}
                  inputMode="decimal"
                  placeholder="0.00"
                  className="w-full rounded-2xl bg-transparent px-3 py-3.5 text-base font-semibold text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                  aria-label="Cash-in amount"
                />
              </div>

              {cashInFormError ? (
                <p className="mt-2 text-sm text-rose-600 dark:text-rose-300">{cashInFormError}</p>
              ) : (
                <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                  The same exact amount is added to your shopper credit only after PayMongo confirms a successful payment.
                </p>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  "GCash",
                  "Maya",
                  "Credit / Debit Card",
                ].map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
                  >
                    {label}
                  </span>
                ))}
              </div>

              <button
                type="submit"
                disabled={cashInSubmitting}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-neutral-900 px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
              >
                <CreditCard size={15} />
                {cashInSubmitting ? "Creating secure checkout..." : "Continue to PayMongo"}
              </button>

              <p className="mt-3 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
                Secured by PayMongo. Cancelled, expired, or failed payments do not change your shopper wallet balance.
              </p>
            </form>

            <div className="rounded-[1.5rem] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 sm:p-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                <ShieldCheck size={12} />
                How It Works
              </div>

              <div className="mt-4 space-y-3">
                <article className="rounded-2xl border border-neutral-200 bg-neutral-50/80 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-800/40">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                    1. Enter the exact amount
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                    Start the cash-in with the exact value you want added to shopper credit.
                  </p>
                </article>

                <article className="rounded-2xl border border-neutral-200 bg-neutral-50/80 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-800/40">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                    2. Pay through PayMongo
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                    Complete the payment on PayMongo using GCash, Maya, or your credit and debit cards.
                  </p>
                </article>

                <article className="rounded-2xl border border-neutral-200 bg-neutral-50/80 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-800/40">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                    3. Balance updates after confirmation
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                    Your shopper credit balance updates only after the payment result is verified on the server.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[1.65rem] border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-4 dark:border-neutral-800 sm:px-5">
          <div>
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Wallet History
            </h2>
          </div>
          {initialWalletLoading ? (
            <SkeletonBlock className="h-7 w-20 rounded-full" />
          ) : (
            <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-[11px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              {transactions.length} entr{transactions.length === 1 ? "y" : "ies"}
            </span>
          )}
        </div>

        <div className="p-4 sm:p-5">
          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          {!error && initialWalletLoading ? (
            <ListSkeleton rows={4} label="Loading wallet activity" />
          ) : null}

          {!error && !loading && transactions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900">
                <Wallet size={18} />
              </div>
              <div className="mt-4 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                No wallet activity yet
              </div>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
                Cash-ins, refund credits, and shopper-credit checkout payments will appear here automatically once they are completed.
              </p>
            </div>
          ) : null}

          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((entry) => {
                const isDebit = String(entry?.direction || "").toUpperCase() === "DEBIT";
                const amountTone = isDebit
                  ? "text-rose-600 dark:text-rose-300"
                  : "text-emerald-600 dark:text-emerald-300";
                const iconTone = isDebit
                  ? "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300"
                  : "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300";
                const statusTone = isDebit
                  ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300";

                return (
                  <article
                    key={entry.id}
                    className="rounded-2xl border border-neutral-200 bg-neutral-50/80 px-4 py-3.5 dark:border-neutral-800 dark:bg-neutral-800/40"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${iconTone}`}>
                            {isDebit ? <ArrowUpRight size={15} /> : <ArrowDownLeft size={15} />}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                              {getTransactionTitle(entry)}
                            </div>
                            <div className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                              {getTransactionSubtitle(entry)}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
                          <span className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2.5 py-1 dark:border-neutral-700 dark:bg-neutral-900">
                            {formatDateTime(entry.createdAt)}
                          </span>
                          {entry?.status ? (
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 ${statusTone}`}>
                              {entry.status}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="text-left sm:text-right">
                        <div className={`text-lg font-semibold ${amountTone}`}>
                          {isDebit ? "-" : "+"}{formatCurrency(entry.amount)}
                        </div>
                        <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                          Balance after: {formatCurrency(entry.balanceAfter)}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
